import { UseFormReturn, useWatch } from "react-hook-form";
import { FormLabel } from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useActiveRegions from "@/features/regions/hooks/use-active-regions";
import { codeToFlag } from "@/features/regions/utils/code-to-flag";
import { GroupedBundleLeaf } from "@/pages/bundles/types/grouped-bundle";
import OfferingCombobox from "../offering-combobox";
import { PaymentPageWizardData } from "./wizard-form";

export type OfferingMode = "link" | "manual";

type Props = {
  form: UseFormReturn<PaymentPageWizardData>;
  mode: OfferingMode;
  // Page-owned: clears the offering + query param when switching to manual.
  setMode: (m: OfferingMode) => void;
  // Page-owned: sets form fields from the picked bundle + syncs the query param.
  onPickBundle: (bundle: GroupedBundleLeaf) => void;
};

export default function StepOffering({
  form,
  mode,
  setMode,
  onPickBundle,
}: Props) {
  const { data: regions } = useActiveRegions();
  // useWatch (hook) so this child re-renders on programmatic setValue
  // (prefill effect / offering pick), not only on a navigation.
  const bundleStdId = useWatch({
    control: form.control,
    name: "bundle_standard_id",
  });
  const regionCode = useWatch({ control: form.control, name: "region" });
  const selectedRegionId =
    useWatch({ control: form.control, name: "region_id" }) ?? "";
  const selectedRegion = regions?.find((r) => r.id === selectedRegionId);

  const tab = (m: OfferingMode, label: string) => (
    <button
      type="button"
      onClick={() => setMode(m)}
      className={`px-3 py-1.5 rounded-md text-sm border ${
        mode === m
          ? "bg-sidebar text-white border-sidebar"
          : "bg-white text-gray-600 border-gray-200"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        {tab("link", "Link an offering")}
        {tab("manual", "No bundle (manual)")}
      </div>

      {mode === "link" ? (
        <div className="space-y-3">
          <FormLabel>
            Linked offering <span className="text-red-500">*</span>
          </FormLabel>
          <OfferingCombobox value={bundleStdId} onSelect={onPickBundle} />
          {bundleStdId && (
            <div className="rounded-md border bg-gray-50 px-4 py-3 text-sm text-gray-700">
              Region {codeToFlag(regionCode)} {regionCode?.toUpperCase()} —
              derived from the offering (locked). Currency &amp; fallback amount
              prefilled.
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <FormLabel>
            Region <span className="text-red-500">*</span>
          </FormLabel>
          <Select
            value={selectedRegionId}
            onValueChange={(id) => {
              const r = regions?.find((x) => x.id === id);
              form.setValue("region_id", id);
              form.setValue("region", r?.code ?? "");
              if (r) form.setValue("currency", r.currency);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a region">
                {selectedRegion
                  ? `${codeToFlag(selectedRegion.code)} ${selectedRegion.display_name}`
                  : undefined}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {(regions ?? []).map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {codeToFlag(r.code)} {r.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500">
            No offering linked — currency &amp; fallback amount are entered
            manually in the next steps.
          </p>
        </div>
      )}
    </div>
  );
}
